/*
Licensed Materials - Property of IBM
694906H
(c) Copyright IBM Corp.  2020 All Rights Reserved

US Government Users Restricted Rights - Use, duplication or disclosure restricted
by GSA ADP Schedule Contract with IBM Corp.
*/

import React, { useEffect } from 'react';
import { Mode, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { useIntl } from '@exo/frontend-common-i18n';
import { ObjectSchema } from 'yup';
import { isRequired } from '../../helpers/isRequired';
import { Field } from '../../layout/Field/Field';
import { TextInput } from '../../fields/TextInput/TextInput';
import { BaseForm } from '../BaseForm/BaseForm';
import { onChangeResolver } from '../../helpers/onChangeResolver';
import { Dropdown } from '../../fields/Dropdown/Dropdown';
import { mapFormErrors } from '../../helpers/mapFormErrors';
import { ApolloError } from '@apollo/client';
import { MandatoryLabel } from '@exo/frontend-components-insurance';

export const AuthCodeForm = React.forwardRef<HTMLFormElement>(
  (
    {
      onSubmit,
      onCancel,
      onError,
      onFail,
      onChange,
      mode = 'onBlur',
      children,
      error,
      errors,
      renderFooter,
      renderBody,
      intlPrefix,
      schema,
      data,
      fields = ['userId', 'companyEmail', 'securityQuestion', 'securityAnswer'],
      idPrefix = 'authCode',
      dataId,
      cancelLabel = 'Cancel',
      saveLabel = 'Save',
      securityQuestions = [
        { name: 'question 1?', value: 'q1' },
        { name: 'question 2?', value: 'q2' },
        { name: 'question 3?', value: 'q3' },
        { name: 'question 4?', value: 'q4' }
      ]
    }: Props,
    ref
  ) => {
    const { register, handleSubmit, formState, control, reset, setError } = useForm({
      mode,
      ...(schema && { resolver: onChangeResolver(onChange, yupResolver(schema)) })
    });
    const intl = useIntl(intlPrefix);

    useEffect(() => {
      if (errors) {
        mapFormErrors(errors.graphQLErrors || [], setError);
      }
      if (
        errors?.graphQLErrors?.some(err => err.extensions?.classification !== 'ValidationError') || error
      ) {
        onFail();
      }
    }, [errors, error]);
    return (
      <BaseForm
        onSubmit={onSubmit}
        onCancel={onCancel}
        onError={onError}
        error={error}
        renderFooter={renderFooter}
        renderBody={renderBody}
        data={data}
        dataId={dataId}
        intlPrefix={intlPrefix}
        cancelLabel={cancelLabel}
        saveLabel={saveLabel}
        form={{ reset, handleSubmit, formState }}
        ref={ref}
      >
        {fields.includes('userId') && (
          <Field>
            <TextInput
              id={`${idPrefix}_userId`}
              {...register('userId', { required: true })}
              control={control}
              autoComplete="off"
              isRequired={isRequired(schema, 'userId', true)}
              requiredLabelText={(isReq: boolean, label: string) => (
                <MandatoryLabel isReq={isReq} label={label} />
              )}
              value={data?.userId}
              errorText={intl.error(formState.errors.userId)}
              labelText={intl.msg('form.userId.label', 'imarket user ID')}
              placeholderText={intl.msg('form.userId.placeholder', ' ')}
            />
          </Field>
        )}

        {fields.includes('companyEmail') && (
          <Field>
            <TextInput
              id={`${idPrefix}_companyEmail`}
              {...register('companyEmail', { required: true })}
              control={control}
              autoComplete="off"
              isRequired={isRequired(schema, 'companyEmail', true)}
              requiredLabelText={(isReq: boolean, label: string) => (
                <MandatoryLabel isReq={isReq} label={label} />
              )}
              value={data?.companyEmail}
              errorText={intl.error(formState.errors.companyEmail)}
              labelText={intl.msg('form.companyEmail.label', 'Company email')}
              placeholderText={intl.msg('form.companyEmail.placeholder', ' ')}
            />
          </Field>
        )}

        {fields.includes('authorisationCode') && (
          
          <Field>
            <TextInput
              id={`${idPrefix}_authorisationCode`}
              {...register('authorisationCode', { required: true })}
              control={control}
              autoComplete="off"
              isRequired={isRequired(schema, 'authorisationCode', true)}
              requiredLabelText={(isReq: boolean, label: string) => (
                <MandatoryLabel isReq={isReq} label={label} />
              )}
              value={data?.authorisationCode}
              errorText={intl.error(formState.errors.authorisationCode)}
              labelText={intl.msg('form.authorisationCode.label', 'Authorisation Code')}
              placeholderText={intl.msg('form.authorisationCode.placeholder', ' ')}
            />
          </Field>
        )}

        {fields.includes('newPassword') && (
          <Field>
            <TextInput
              id={`${idPrefix}_newPassword`}
              {...register('newPassword', { required: true })}
              control={control}
              type="password"
              autoComplete="off"
              isRequired={isRequired(schema, 'password', true)}
              requiredLabelText={(isReq: boolean, label: string) => (
                <MandatoryLabel isReq={isReq} label={label} />
              )}
              value={data?.newPassword}
              errorText={intl.error(formState.errors.newPassword)}
              labelText={intl.msg('form.newPassword.label', 'Password')}
              placeholderText={intl.msg('form.newPassword.placeholder', ' ')}
            />
          </Field>
        )}

        {fields.includes('newPasswordConfirmed') && (
          <Field>
            <TextInput
              id={`${idPrefix}_newPasswordConfirmed`}
              {...register('newPasswordConfirmed', { required: true })}
              control={control}
              autoComplete="off"
              type="password"
              isRequired={isRequired(schema, 'newPasswordConfirmed', true)}
              requiredLabelText={(isReq: boolean, label: string) => (
                <MandatoryLabel isReq={isReq} label={label} />
              )}
              value={data?.newPasswordConfirmed}
              errorText={intl.error(formState.errors.newPasswordConfirmed)}
              labelText={intl.msg('form.newPasswordConfirmed.label', 'Confirm Password')}
              placeholderText={intl.msg('form.newPasswordConfirmed.placeholder', ' ')}
            />
          </Field>
        )}

        {fields.includes('securityQuestion') && (
          <Field>
            <Dropdown
              id={`${idPrefix}_securityQuestionId`}
              {...register('securityQuestionId', { required: true })}
              control={control}
              isRequired={isRequired(schema, 'securityQuestionId', true)}
              requiredLabelText={(isReq: boolean, label: string) => (
                <MandatoryLabel isReq={isReq} label={label} />
              )}
              value={data?.securityQuestionId}
              errorText={intl.error(formState.errors.securityQuestionId)}
              labelText={intl.msg('form.securityQuestionId.label', 'Security question')}
              placeholderText={intl.msg('form.securityQuestionId.placeholder', ' ')}
              items={securityQuestions}
            />
          </Field>
        )}

        {fields.includes('securityAnswer') && (
          <Field>
            <TextInput
              id={`${idPrefix}_securityAnswer`}
              {...register('securityAnswer', { required: true })}
              control={control}
              isRequired={isRequired(schema, 'securityAnswer', true)}
              requiredLabelText={(isReq: boolean, label: string) => (
                <MandatoryLabel isReq={isReq} label={label} />
              )}
              autoComplete="off"
              value={data?.securityAnswer}
              errorText={intl.error(formState.errors.securityAnswer)}
              labelText={intl.msg('form.securityAnswer.label', 'securityAnswer Label')}
              placeholderText={intl.msg('form.securityAnswer.placeholder', 'securityAnswer Label')}
            />
          </Field>
        )}
        {children}
      </BaseForm>
    );
  }
);

type Props = {
  onSubmit: (data: any) => void;
  onError?: (error: any) => void;
  onSuccess: () => void;
  onFail: () => void;
  errors?: ApolloError;
  onCancel?: () => void;
  onChange?: (data: any) => void;
  error?: string;
  intlPrefix?: string;
  children?: any;
  renderFooter?: (state: any) => React.ReactElement;
  renderBody?: (state: any) => React.ReactElement;
  data?: Data;
  mode?: Mode;
  cancelLabel?: string;
  saveLabel?: string;
  schema?: ObjectSchema;
  loadItems?: (input: string) => Promise<{ value: string; name: string }[]>;
  fields?: string[];
  dataId?: string;
  idPrefix?: string;
  disabledFields?: string[];
  securityQuestions?: {
    name: string;
    value: string;
  }[];
};

type Data = {
  userId?: string;
  companyEmail?: string;
  securityQuestionId?: string;
  securityAnswer?: string;
  authorisationCode?: string;
  newPassword?: string;
  newPasswordConfirmed?: string;
};
